import { StackedLayoutTemplate } from 'components/atomic/templates/stacked-layout';
import InternalLinks from 'components/home-page/internal-links';
import { OpenHouseCard, OPEN_HOUSE_CARD_VARIANTS } from 'ui/open-house';
import { ListingHeaderSection, ListingActionsSection } from 'sections';
import { useGalleryWithBadges } from 'sections/listing/gallery';
import { HeroMobileSwitch } from 'sections/listing/hero';
import { useHeaderHeight } from 'hooks/use-header-height';
import { StickyHeaderTabs, TAB_BAR_HEIGHT } from 'sections/listing/sticky-header-tabs';
import { useListingSections } from 'hooks/use-listing-sections';
import { useListingSectionVisibility } from '../hooks/use-listing-section-visibility';
import { useExperimentElementVisibility, ExperimentElement } from 'experiments/element-visibility';
import { hasFeatureFlag, FEATURE_FLAGS } from '../../types';
import { ContentSection } from '../sections/content';
import SimilarListingsSections from '../../components/similar-listings-sections';
import { prepareHeroProps, prepareEventHandlers } from './utils';
import type { AddressPageV2ViewProps } from '../address-page-v2-view';
import { getSectionId, SECTION_IDS } from '../utils/get-section-id';
import similarListingsStyles from './similar-listings-section.module.scss';

/**
 * Mobile Layout for Address Page V2
 * 
 * Layout structure:
 * - Stacked vertical layout
 * - Sticky footer with action buttons (Request Info, Book Showing)
 */
export function MobileLayout({ data, handlers }: AddressPageV2ViewProps) {
  const { 
    galleryData, 
    complianceData, 
    openHouseData, 
    stickyFooterData, 
    commonData, 
    isBreadcrumbsSectionVisible, 
    headerData, 
  } = data;
  
  const { listing, internalLinks, flags, tenantId } = commonData;
  const { onImageClick, onComplianceClick, onImageSwipe } = handlers;

  // Element visibility - controls visibility based on experiments and viewport
  const visibility = useExperimentElementVisibility();

  // Prepare data for child components
  const galleryDataWithBadges = useGalleryWithBadges(galleryData, tenantId, listing);
  const heroProps = prepareHeroProps(data, handlers);
  const eventHandlers = prepareEventHandlers(data, handlers);

  // Calculate scroll offset for sticky tab navigation (header + tab bar + buffer)
  const headerHeight = useHeaderHeight();
  const scrollOffset = headerHeight + TAB_BAR_HEIGHT + 16;

  // Viewport-specific props for content section
  const isMapInteractive = false; // Mobile: maps are NOT interactive
  const showMyListingAgent = true;

  // Calculate visibility flags for listing sections (tabs)
  const visibilityFlags = useListingSectionVisibility(commonData);
  // Hide commute section in the sticky tab variation
  const sectionsVisibilityFlags = {
    ...visibilityFlags,
    shouldShowCommute: false,
  };
  const listingSections = useListingSections({ visibilityFlags: sectionsVisibilityFlags });

  // Check if we should render similar listings sections
  const hasAddressCoordinates = listing.position?.coordinates?.length === 2;
  const showStreetLevelLocation = listing.displayAddress !== false;
  const shouldShowSimilarListings = 
    (hasFeatureFlag(flags, FEATURE_FLAGS.SIMILAR_LISTINGS_ENABLED) || 
     hasFeatureFlag(flags, FEATURE_FLAGS.COMPARABLE_LISTINGS_ENABLED)) && 
    hasAddressCoordinates &&
    showStreetLevelLocation;

  // Similar listings element - rendered with section ID for tab navigation
  const similarListingsSection = shouldShowSimilarListings ? (
    <section 
      id={getSectionId(SECTION_IDS.SIMILAR_LISTINGS, 'mobile')}
      className={similarListingsStyles.similarListingsSection}
      style={{ '--scroll-offset': `${scrollOffset}px` } as React.CSSProperties}
    >
      <SimilarListingsSections
        listing={listing}
        showCarousel
        hideSimilarListings={!hasFeatureFlag(flags, FEATURE_FLAGS.SIMILAR_LISTINGS_ENABLED)}
        hideComparableListings={!hasFeatureFlag(flags, FEATURE_FLAGS.COMPARABLE_LISTINGS_ENABLED)}
        onListingCardClick={handlers.onSimilarListingsCardClick}
      />
    </section>
  ) : null;

  return (
    <StackedLayoutTemplate
      stickyFooter={
        visibility.shouldShow(ExperimentElement.STICKY_FOOTER) ? (
          <ListingActionsSection 
            sectionId="listing-actions-sticky-footer" 
            data={stickyFooterData} 
          />
        ) : null
      }
    >
      {isBreadcrumbsSectionVisible && (
        <ListingHeaderSection sectionId="listing-header" data={headerData} />
      )}
      
      <HeroMobileSwitch
        {...heroProps}
        listing={listing}
        gallery={galleryDataWithBadges}
        complianceData={complianceData}
        onImageClick={onImageClick}
        onComplianceClick={onComplianceClick}
        onSlideChange={onImageSwipe}
      />
      
      {/* Open House Card: Visibility manager controls visibility based on LISTING_HERO experiment */}
      {visibility.shouldShow(ExperimentElement.OPEN_HOUSE_CARD) && openHouseData.hasValue && (
        <OpenHouseCard data={openHouseData} variant={OPEN_HOUSE_CARD_VARIANTS.MOBILE} />
      )}

      {/* Sticky Header Tabs: Visibility manager controls visibility based on STICKY_HEADER_TABS experiment */}
      {visibility.shouldShow(ExperimentElement.STICKY_HEADER_TABS) && (
        <StickyHeaderTabs 
          sections={listingSections}
          onTabClick={handlers.onStickyHeaderTabClick}
          iconPosition="top"
          showIconsWhenSticky={true}
          viewport="mobile"
        />
      )}
      
      <ContentSection 
        data={commonData} 
        handlers={eventHandlers}
        isMapInteractive={isMapInteractive}
        showMyListingAgent={showMyListingAgent}
        viewport="mobile"
        priceHistoryRef={data.priceHistoryRef}
      />
      
      {similarListingsSection}
      
      <section>
        <InternalLinks 
          isActive={hasFeatureFlag(flags, FEATURE_FLAGS.INTERNAL_LINKS_ACTIVE)} 
          data={internalLinks} 
          heading="" 
        />
      </section>
    </StackedLayoutTemplate>
  );
}
